import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { PageHeader, Button } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import apiClient, { ApiError } from '@/services/apiClient';
import { confirmMedicrewPayment } from '@/services/paymentConfirmApi';
import { invalidateEnrolledClassListQueries, classKeys } from '@/data/queries/useClasses';
import { parseCartStorage, persistCartItems } from '@/utils/cartStorage';
import { readTossCheckoutPending, clearTossCheckoutPending } from '@/lib/tossPendingCheckout';
import { parseMedicrewOrderIdFromTossOrderId } from '@/services/tossCheckout';
import { formatCurrency } from '@/lib/currencyFormat';

/**
 * Toss successUrl 리다이렉트 — 쿼리: paymentKey, orderId, amount
 */
export default function PaymentTossSuccess(): React.ReactElement {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const [status, setStatus] = useState<'running' | 'ok' | 'error'>('running');
    const [message, setMessage] = useState('결제를 확인하는 중입니다…');
    /** 승인 API 실패 시 고객센터·로그용 보조 문구 */
    const [supportHint, setSupportHint] = useState<string | null>(null);
    const [doneRedirectBase, setDoneRedirectBase] = useState<'student' | 'master'>('student');

    const pageTitle =
        status === 'running' ? '결제 확인 중' : status === 'ok' ? '결제 완료' : '결제 확인 실패';

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            const paymentKey = searchParams.get('paymentKey') ?? '';
            const orderId = searchParams.get('orderId') ?? '';
            const amountRaw = searchParams.get('amount') ?? '';
            const amount = Number(amountRaw);
            if (!paymentKey || !orderId || !Number.isFinite(amount) || amount <= 0) {
                setStatus('error');
                setSupportHint(null);
                setMessage('결제 정보가 올바르지 않습니다. 장바구니에서 다시 시도해 주세요.');
                return;
            }

            const pending = readTossCheckoutPending();
            /** 결제 직전 저장한 가맹점 주문번호(주로 open-class `orderNumber`)와 토스가 돌려준 쿼리가 같아야 함 */
            if (pending && orderId !== pending.tossMerchantOrderId) {
                setStatus('error');
                setSupportHint(null);
                setMessage('주문 정보가 일치하지 않습니다. 고객센터로 문의해 주세요.');
                return;
            }
            const orderIdFromUrl = parseMedicrewOrderIdFromTossOrderId(orderId);
            if (pending && Number(pending.amount) !== Number(amount)) {
                setStatus('error');
                setSupportHint(null);
                setMessage('결제 금액이 주문 정보와 일치하지 않습니다. 고객센터로 문의해 주세요.');
                return;
            }

            /** Swagger `orderId`(숫자): open-class 직후 저장한 값 우선, 없으면 URL에서만 추출 */
            const medicrewOrderIdNumeric = pending?.medicrewOrderId ?? orderIdFromUrl;
            if (medicrewOrderIdNumeric == null || !Number.isFinite(medicrewOrderIdNumeric) || medicrewOrderIdNumeric <= 0) {
                setStatus('error');
                setSupportHint(null);
                setMessage('주문 번호를 확인할 수 없습니다. 장바구니에서 다시 시도해 주세요.');
                return;
            }

            if (!apiClient.hasTokens()) {
                setStatus('error');
                setSupportHint(null);
                setMessage(
                    '로그인 세션이 없어 결제 확정을 요청할 수 없습니다. 결제를 진행한 브라우저에서 로그인이 유지됐는지 확인한 뒤, 다시 시도하거나 고객센터로 문의해 주세요.'
                );
                return;
            }

            try {
                // pending 또는 기본값 KRW 사용
                const currency = pending?.currency || 'KRW';
                await confirmMedicrewPayment(
                    {
                        paymentKey,
                        orderId: medicrewOrderIdNumeric,
                        amount,
                    },
                    currency
                );
            } catch (e) {
                if (!cancelled) {
                    setStatus('error');
                    const pkShort = paymentKey.length > 12 ? `${paymentKey.slice(0, 8)}…` : paymentKey;
                    if (e instanceof ApiError) {
                        setMessage(e.message || '결제 승인에 실패했습니다.');
                        setSupportHint(
                            `고객센터 문의 시 알려 주세요: 주문 ID ${medicrewOrderIdNumeric}, 금액 ${formatCurrency(amount, 'KRW')}, paymentKey ${pkShort}` +
                                (e.status ? `, HTTP ${e.status}` : '')
                        );
                    } else {
                        setMessage(e instanceof Error ? e.message : '결제 승인에 실패했습니다.');
                        setSupportHint(
                            `고객센터 문의 시 알려 주세요: 주문 ID ${medicrewOrderIdNumeric}, 금액 ${formatCurrency(amount, 'KRW')}, paymentKey ${pkShort}`
                        );
                    }
                }
                return;
            }

            const ids = pending?.selectedClassIds ?? [];
            const cartEntries = parseCartStorage(localStorage.getItem('cart'));
            const removeIds = new Set(ids.map(String));
            const remaining = cartEntries.filter((e) => !removeIds.has(String(e.id)));
            persistCartItems(remaining);

            // 결제 승인 시 백엔드에서 자동으로 classUserMapping 생성하므로
            // 별도 enrollClass API 호출 불필요 (중복 호출 시 409 Conflict 발생)

            await invalidateEnrolledClassListQueries(queryClient);
            await queryClient.invalidateQueries({ queryKey: ['orders'], exact: false });
            // 클래스 상세 페이지에서 isEnrolled 상태가 즉시 반영되도록 상세 쿼리도 무효화
            await queryClient.invalidateQueries({ queryKey: classKeys.details(), exact: false });

            if (!cancelled) {
                setDoneRedirectBase(pending?.redirectBase === 'master' ? 'master' : 'student');
                clearTossCheckoutPending();
                setSupportHint(null);
                setStatus('ok');
                setMessage('결제가 완료되었습니다.');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [searchParams, queryClient]);

    const base = doneRedirectBase === 'master' ? ROUTES.MASTER.BASE : ROUTES.STUDENT.BASE;

    return (
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
            <PageHeader title={pageTitle} />
            {status === 'running' && (
                <div className="mt-8 flex flex-col items-center gap-3 text-gray-600">
                    <Loader2 className="w-10 h-10 animate-spin text-brand-500" />
                    <p>{message}</p>
                </div>
            )}
            {status === 'ok' && (
                <div className="mt-8 space-y-6">
                    <p className="text-gray-800">{message}</p>
                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
                        <Button variant="primary" onClick={() => navigate(`${base}/my-classes`)}>
                            마이 클래스로 이동
                        </Button>
                        <Button variant="outline" onClick={() => navigate(`${ROUTES.STUDENT.BASE}/orders`)}>
                            주문 내역 보기
                        </Button>
                    </div>
                </div>
            )}
            {status === 'error' && (
                <div className="mt-8 space-y-4 text-left">
                    <p className="text-red-600 font-medium">{message}</p>
                    {supportHint && (
                        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                            {supportHint}
                        </p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        <Button variant="outline" onClick={() => navigate(ROUTES.COMMON.CART)}>
                            장바구니로 돌아가기
                        </Button>
                        <Button variant="outline" onClick={() => navigate(`${ROUTES.STUDENT.BASE}/orders`)}>
                            주문 내역 보기
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
